import { useEffect, useState } from "react";
import * as Sentry from "@sentry/react-native";
import { supabase } from "@lezz/supabase";
import { posthog } from "@/shared/lib/posthog";
import { authTrace } from "../lib/auth-trace";
import type { Session } from "@supabase/supabase-js";

/**
 * Hook to manage auth session state.
 * Relies on onAuthStateChange (fires INITIAL_SESSION on subscription) as the
 * single source of truth — avoids a race between fetchClaims and live events.
 */
export function useAuthSession() {
  // undefined = still resolving, null = no session, Session = authenticated
  const [session, setSession] = useState<Session | undefined | null>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const span = authTrace.sessionStart();

    const fetchSession = async () => {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (error) {
        authTrace.sessionError(span, error);
        setSession(null);
        setIsLoading(false);
        return;
      }

      setSession(session ?? null);
      setIsLoading(false);
      authTrace.sessionResolved(span, {
        hasSession: Boolean(session),
        userId: session?.user.id,
      });
      return;
    };

    fetchSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      const changeSpan = authTrace.sessionChangeStart(event);

      if (!session) {
        authTrace.sessionResolved(changeSpan, { hasSession: false });
        setSession(null);
        setIsLoading(false);
        Sentry.setUser(null);
        posthog?.reset();
        return;
      }

      setSession(session);
      setIsLoading(false);
      Sentry.setUser({ id: session.user.id });
      posthog?.identify(session.user.id, {
        $set: { email: session.user.email ?? null },
      });
      authTrace.sessionResolved(changeSpan, {
        hasSession: true,
        userId: session.user.id,
      });
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return {
    session: session ?? null,
    isLoading,
    isLoggedIn: Boolean(session),
  };
}
